import { useEffect, useRef, useState } from "react";
import { Alert, Box, Card, CardContent, CircularProgress, Typography } from "@mui/material";
import { useItsOneView } from "@/hooks/useItsOneView";
import type { ItsOneViewSectionKey } from "@/types/its-one-view";
import ItsSearchCard from "./components/ItsSearchCard";
import FamilyMembersStrip from "./components/FamilyMembersStrip";
import SectionSidebar from "./components/SectionSidebar";
import SectionPanel from "./components/SectionPanel";

const ITS_PATTERN = /^\d{8}$/;

export default function ItsOneViewDashboard() {
  const { data, loading, error, search, reset, lastItsNo } = useItsOneView();
  const [activeSection, setActiveSection] = useState<ItsOneViewSectionKey>("fmbTakhmeen");
  const restoredSearchRef = useRef(false);

  useEffect(() => {
    if (restoredSearchRef.current || !ITS_PATTERN.test(lastItsNo)) return;
    restoredSearchRef.current = true;
    void search(lastItsNo);
  }, [lastItsNo, search]);

  const handleSearch = async (itsNo: string) => {
    await search(itsNo);
    setActiveSection("fmbTakhmeen");
  };

  const handleReset = () => {
    restoredSearchRef.current = true;
    reset();
    setActiveSection("fmbTakhmeen");
  };

  return (
    <Box sx={{ pb: 4 }}>
      <Card variant="outlined" sx={{ mb: 2, overflow: "hidden" }}>
        <CardContent
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            alignItems: { xs: "stretch", md: "flex-start" },
            py: 2,
            overflow: "hidden",
            "&:last-child": { pb: 2 },
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: { xs: "100%", md: 300 },
              pr: { md: 2 },
              borderRight: { md: 1 },
              borderColor: { md: "divider" },
            }}
          >
            <ItsSearchCard
              loading={loading}
              initialIts={lastItsNo}
              onSearch={handleSearch}
              onReset={handleReset}
            />
          </Box>

          {loading && !data ? (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                maxWidth: { md: 560 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 2,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : null}

          {data ? (
            <FamilyMembersStrip
              members={data.familyMembers}
              hofIts={data.hofIts}
              hofName={data.hofName}
              lookupIts={data.lookupIts}
            />
          ) : !loading ? (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                maxWidth: { md: 560 },
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                pl: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Search to load household members here.
              </Typography>
            </Box>
          ) : null}
        </CardContent>
      </Card>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {data ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            alignItems: "flex-start",
          }}
        >
          <SectionSidebar
            active={activeSection}
            onChange={setActiveSection}
            sectionAccess={data.sectionAccess}
            sections={data.sections}
          />
          <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            <SectionPanel
              sectionKey={activeSection}
              sectionAccess={data.sectionAccess}
              sections={data.sections}
            />
          </Box>
        </Box>
      ) : !loading && !error ? (
        <Alert severity="info">
          Search by ITS to see household accounts, pending amounts, and receipts.
        </Alert>
      ) : null}
    </Box>
  );
}
